/**
 * @fileoverview Global Staff Payments Component
 *
 * This component displays all payments across the entire application with advanced filtering,
 * searching, and bulk operations. It provides comprehensive payment management for staff users.
 *
 * Key Characteristics:
 * - Scope: All payments app-wide across all organizations and reports
 * - Data Source: GET_PAYMENTS query (dedicated payments endpoint)
 * - Grid Type: OmnisearchDataGrid with advanced filtering and search capabilities
 * - Location: Standalone page at /staff/payments
 * - Features: Multi-tenant view, bulk operations, advanced search, comprehensive payment data
 *
 * This is distinct from the report-specific payments view (ReportPayments) which shows
 * only payments for a single Tier II report within the report details context.
 *
 * @see ReportPayments - For single report payment management
 * @see PaymentDialog - Shared payment creation/editing form
 * @see ReconcilePaymentDialog - Shared reconciliation functionality
 */

import { useApolloClient } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Clear from "@mui/icons-material/Clear";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Mode from "@mui/icons-material/Mode";
import Warning from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { PaymentDialog } from "components/Payment/PaymentDialog";
import { ReconcilePaymentDialog } from "components/ReconcilePaymentDialog";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  JobStatus,
  LobMailStatus,
  LobMailType,
  Payee,
  PaymentMethod,
  PaymentStatus,
  PaymentsQuery,
  PaymentsQueryVariables,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { GET_PAYMENTS, useCancelPayment } from "hooks/usePayment";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";

type PaymentRow = PaymentsQuery["payments"]["items"][number];

export const GlobalPayments = () => {
  const alerts = useAlerts();
  const apolloClient = useApolloClient();
  const { tenantId } = useTenant();
  const { isStaff } = useCurrentUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    selectedId: paymentId,
    pushIdToUrl,
    popIdFromUrl,
  } = useSelectedIdInUrl("paymentId");

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [cancelPayment, setCancelPayment] = useState<PaymentRow | undefined>(
    undefined
  );
  const [editPayment, setEditPayment] = useState<PaymentRow | undefined>(
    undefined
  );
  const [showReconcileDialog, setShowReconcileDialog] = useState(false);
  const [paymentsToReconcile, setPaymentsToReconcile] = useState<PaymentRow[]>(
    []
  );
  const [isBulkOperation, setBulkOperation] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState<PaymentRow[]>([]);

  // Track previous paymentId to detect navigation changes
  const prevPaymentIdRef = useRef<string | undefined>(undefined);
  // Track if we're in the process of closing the dialog
  const isClosingRef = useRef(false);

  /**
   * Deep Linking Support for Payment Dialog
   *
   * This effect handles opening the payment dialog when navigating to a URL
   * with a payment ID (e.g., /staff/payments/payment-123).
   *
   * Race Condition Fix:
   * Previously, when closing the dialog, there was a race condition where:
   * 1. closeModal() would set showPaymentForm=false (synchronous)
   * 2. This effect would immediately run and see paymentId still in URL
   * 3. The effect would reopen the dialog
   * 4. Only then would popIdFromUrl() complete (asynchronous)
   *
   * Solution:
   * - isClosingRef tracks when we're in the process of closing
   * - The effect skips reopening when isClosingRef.current is true
   * - The flag resets once both URL and dialog state have stabilized
   *
   * Supported scenarios:
   * - Direct navigation to payment URL (deep link)
   * - Navigation between different payments
   * - Closing dialog returns to grid without reopening
   */
  useEffect(() => {
    const prevPaymentId = prevPaymentIdRef.current;

    // Only open dialog when:
    // 1. We have a paymentId in the URL
    // 2. Dialog is not already open
    // 3. We're not in the process of closing the dialog
    // 4. Either:
    //    a. This is a different paymentId than before (navigating between payments)
    //    b. We're going from no ID to an ID (initial deep link)
    if (
      paymentId &&
      !showPaymentForm &&
      !isClosingRef.current &&
      (paymentId !== prevPaymentId || !editPayment)
    ) {
      // Create a minimal payment object with just the ID for editing
      setEditPayment({ id: paymentId } as PaymentRow);
      setShowPaymentForm(true);
    }

    // Reset closing flag when dialog state stabilizes
    if (!paymentId && !showPaymentForm) {
      isClosingRef.current = false;
    }

    // Update the ref for next render
    prevPaymentIdRef.current = paymentId;
  }, [paymentId, showPaymentForm, editPayment]);

  const closeModal = useCallback(async () => {
    // Mark that we're closing to prevent the useEffect from reopening
    // This prevents a race condition where the dialog would reopen because
    // the URL still contains the paymentId when the effect runs after state changes
    isClosingRef.current = true;

    setShowPaymentForm(false);
    setShowCancelConfirmation(false);
    setShowReconcileDialog(false);
    setEditPayment(undefined);
    setCancelPayment(undefined);
    setPaymentsToReconcile([]);
    setBulkOperation(false);
    setSelectedPayments([]);

    // Use the hook's popIdFromUrl instead of manual navigation
    // Clear URL if we have a paymentId (came from deep link) or editPayment
    if (paymentId || editPayment) {
      popIdFromUrl();
    }
  }, [paymentId, editPayment, popIdFromUrl]);

  const [cancelPaymentMutation, { loading: cancelling }] = useCancelPayment({
    refetchQueries: [GET_PAYMENTS],
  });

  const openPaymentsForm = (payment?: PaymentRow) => {
    if (payment) {
      setEditPayment(payment);
      // Use the hook's pushIdToUrl instead of manual navigation
      pushIdToUrl(payment.id);
    }
    setShowPaymentForm(true);
  };

  const openCancelConfirmation = (payment: PaymentRow) => {
    setCancelPayment(payment);
    setShowCancelConfirmation(true);
  };

  const openReconcileDialog = (
    payments: PaymentRow[],
    isBulkOperation = false
  ) => {
    setPaymentsToReconcile(payments);
    setBulkOperation(isBulkOperation);
    setShowReconcileDialog(true);
  };

  const handleBulkReconcile = () => {
    if (selectedPayments.length > 0) {
      openReconcileDialog(selectedPayments, true); // Pass all selected payments
    }
  };

  const handleSingleReconcile = (paymentId: string) => {
    // Create a minimal payment object for reconciliation
    const payment = { id: paymentId } as PaymentRow;
    openReconcileDialog([payment]);
  };

  const handleCancelPayment = useCallback(async () => {
    if (!cancelPayment) return;
    await cancelPaymentMutation({
      variables: { id: cancelPayment.id },
      refetchQueries: [GET_PAYMENTS],
      onCompleted() {
        alerts.success("Successfully cancelled the payment");
      },
      onError(err) {
        alerts.error("There was an error cancelling this payment", err);
      },
    });
    closeModal();
  }, [alerts, closeModal, cancelPayment, cancelPaymentMutation]);

  const dialogTenantId = editPayment?.tenantId ?? tenantId;

  // For staff users, allow creating payments even without a pre-selected tenant
  // since the PaymentDialog handles tenant selection internally
  const canCreatePayment = isStaff || !!dialogTenantId;

  const lobType = useMemo(
    () =>
      cancelPayment?.lobMail?.type === LobMailType.Check ? "checks" : "letters",
    [cancelPayment?.lobMail?.type]
  );

  useBreadcrumb({
    label: "Payments",
  });

  // Bulk actions component
  const BulkActionsMenu = ({
    selectedRows,
  }: {
    selectedRows: PaymentRow[];
  }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const buttonId = "payments-bulk-actions-button";
    const menuId = "payments-bulk-actions-menu";

    return (
      <>
        <Button
          id={buttonId}
          aria-controls={open ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          variant="contained"
          onClick={handleClick}
          endIcon={<ExpandMore />}
          disabled={selectedRows.length === 0}
        >
          Bulk Actions
        </Button>
        <Menu
          id={menuId}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": buttonId,
          }}
        >
          <MenuList dense>
            <MenuItem
              key="update-reconciliation-status"
              onClick={() => {
                handleBulkReconcile();
                handleClose();
              }}
              disabled={selectedRows.length === 0}
            >
              <ListItemIcon>
                <CheckCircle />
              </ListItemIcon>
              <ListItemText>Update Reconciliation Status</ListItemText>
            </MenuItem>
          </MenuList>
        </Menu>
      </>
    );
  };

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<PaymentsQuery, PaymentsQueryVariables, PaymentRow>
        persistenceKey="staffPayments-v2"
        isURLDriven
        checkboxSelection
        disableRowSelectionOnClick
        initialSortModel={[{ field: "paidAt", sort: "desc" }]}
        columnVisibilityModel={{
          id: false, // Hidden by default - internal ID not useful for users
          "lobMail.id": false, // Hidden by default - internal Lob ID not useful for users
          numFacilities: false, // Hidden by default - count not essential for primary workflow
          numTierIIReports: false, // Hidden by default - count not essential for primary workflow
        }}
        columns={[
          {
            field: "id",
            headerName: "ID",
            flex: 0.5,
          },
          {
            field: "status",
            headerName: "Payment Status",
            flex: 0.5,
            filterKeyType: "enumAutocomplete",
            enumValues: Object.values(PaymentStatus),
          },
          {
            field: "job.status",
            headerName: "Job Status",
            flex: 0.5,
            filterKeyType: "enumAutocomplete",
            enumValues: Object.values(JobStatus),
            renderCell: (params) => (
              <Link
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/staff/jobs/${params.row.job?.id}`);
                }}
              >
                {params.row.job?.status}
              </Link>
            ),
          },
          {
            field: "lobMail.checkSent",
            headerName: "Check Sent To Lob",
            flex: 0.5,
            valueGetter(_, row) {
              if (row?.paymentMethod === PaymentMethod.Check) {
                return row.lobMail?.lobId ? "Yes" : "No";
              }

              return "N/A";
            },
          },
          {
            field: "lobMail.lobId",
            headerName: "Lob ID",
            flex: 0.5,
            valueGetter(_, row) {
              if (row?.paymentMethod === PaymentMethod.Check) {
                return row.lobMail?.lobId;
              }

              return "N/A";
            },
          },
          {
            field: "lobMail.status",
            headerName: "Lob Status",
            flex: 0.5,
            valueGetter(_, row) {
              if (row?.paymentMethod === PaymentMethod.Check) {
                return row.lobMail?.status;
              }
            },
            renderCell(params) {
              return prettyPrintEnumValue(params.value);
            },
            filterKeyType: "enumAutocomplete",
            enumValues: Object.values(LobMailStatus),
          },
          {
            field: "paymentMethod",
            headerName: "Payment Method",
            flex: 0.5,
            renderCell(params) {
              return prettyPrintEnumValue(params.value);
            },
            filterKeyType: "enumAutocomplete",
            enumValues: Object.values(PaymentMethod),
          },
          {
            field: "tenant",
            headerName: "Organization",
            valueGetter(_, row) {
              return row.tenant?.name;
            },
            flex: 1,
            filterKeyType: "multipleTenant",
          },
          { field: "payee", headerName: "Payee", flex: 1 },
          {
            field: "amount",
            headerName: "Amount",
            flex: 1,
            valueFormatter(value) {
              return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(value);
            },
            filterKeyType: "number",
          },
          { field: "recordedByEmail", headerName: "Recorded By", flex: 1 },
          {
            field: "payeeType",
            headerName: "Payee Type",
            flex: 0.5,
            filterKeyType: "enumAutocomplete",
            enumValues: Object.values(Payee),
          },
          {
            field: "paidAt",
            headerName: "Paid At",
            flex: 1,
            renderCell(params) {
              return prettyPrintDateTime(params.value);
            },
            filterKeyType: "time",
          },
          {
            field: "numFacilities",
            headerName: "# Facilities",
            valueGetter(_, row) {
              return row.facilities?.length ? row.facilities.length : null;
            },
            align: "center",
            flex: 0.2,
          },
          {
            field: "numTierIIReports",
            headerName: "# Tier II Reports",
            valueGetter(_, row) {
              return row.tierIIReports?.length
                ? row.tierIIReports.length
                : null;
            },
            align: "center",
            flex: 0.2,
          },
          {
            field: "billable",
            headerName: "Billable",
            flex: 0.3,
            renderCell(params) {
              const billable = params.row.billable;
              return billable ? <Check color="success" /> : null;
            },
            filterKeyType: "boolean",
          },
          {
            field: "reconciliationStatus",
            headerName: "Reconciled",
            flex: 0.3,
            renderCell(params) {
              const reconciliationStatus = params.row.reconciliationStatus;

              const isReconciled = reconciliationStatus === "RECONCILED";
              const isProblem = reconciliationStatus === "PROBLEM";

              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isReconciled ? (
                    <Check color="success" />
                  ) : isProblem ? (
                    <Warning color="warning" />
                  ) : null}
                </Box>
              );
            },
            filterKeyType: "enumAutocomplete",
            enumValues: ["UNRECONCILED", "RECONCILED", "PROBLEM"],
          },
          {
            type: "actions",
            field: "actions",
            align: "left",
            getActions({ row }) {
              const actions = [
                <GridActionsCellItem
                  key="edit"
                  label="Edit"
                  icon={<Mode />}
                  onClick={() => openPaymentsForm(row)}
                />,
              ];

              if (row.status !== PaymentStatus.Cancelled) {
                actions.push(
                  <GridActionsCellItem
                    key="cancel"
                    label="Cancel"
                    icon={<Clear />}
                    onClick={() => openCancelConfirmation(row)}
                  />
                );
              }

              return actions;
            },
          },
        ]}
        excludeFilterColumns={["numFacilities", "numTierIIReports"]}
        dataQuery={GET_PAYMENTS}
        getItems={(data) => data.payments.items}
        getCount={(data) => data.payments.count}
        onSelectedRowsChanged={setSelectedPayments}
        commandButtons={[
          <ExportDataButton
            key="export"
            exportType="payments"
            tenantId={tenantId}
            fileName={`payments-${new Date().toISOString().split("T")[0]}`}
            filter={
              searchParams.get("omnisearch")
                ? { search: searchParams.get("omnisearch") }
                : undefined
            }
            title="Export"
          />,
          <BulkActionsMenu
            key="bulk-actions"
            selectedRows={selectedPayments}
          />,
          <Button
            key="add"
            variant="contained"
            onClick={() => openPaymentsForm()}
            disabled={!canCreatePayment}
          >
            {canCreatePayment
              ? "Make or record a payment"
              : "Select organization to make a payment"}
          </Button>,
        ]}
        onRowClick={({ row }) => openPaymentsForm(row)}
      />
      {showPaymentForm && (
        <PaymentDialog
          open={showPaymentForm}
          onClose={closeModal}
          tenantId={dialogTenantId}
          onCompleted={async () => {
            await apolloClient.refetchQueries({ include: [GET_PAYMENTS] });
          }}
          paymentId={editPayment?.id}
          onReconcilePayment={handleSingleReconcile}
        />
      )}
      {showCancelConfirmation && (
        <ConfirmDialog
          header="Are you sure you want to cancel this payment?"
          resources={[
            {
              resourceType: "Payment",
              resourceName: cancelPayment?.id,
            },
          ]}
          confirmText="Yes, Cancel"
          cancelText="No, Keep Payment"
          info={`
            This will cancel the payment.
            This will not cancel any in-flight lob checks.
            If you need to cancel a check,
            please do so in Lob or contact #engineering-support in Slack. 
            ${
              cancelPayment?.lobMail
                ? `Lob URL: https://dashboard.lob.com/${lobType}/${cancelPayment.lobMail.lobId}`
                : ""
            }
            `}
          open={showCancelConfirmation}
          onClose={closeModal}
          onConfirm={handleCancelPayment}
          loading={cancelling}
        />
      )}
      {showReconcileDialog && (
        <ReconcilePaymentDialog
          open={showReconcileDialog}
          onClose={closeModal}
          payments={paymentsToReconcile as any[]}
          isBulkOperation={isBulkOperation}
          onReconciled={async () => {
            await apolloClient.refetchQueries({ include: [GET_PAYMENTS] });
          }}
        />
      )}
    </Stack>
  );
};
